import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Send, FileText, Loader2 } from 'lucide-react';
import { generateClaudeResponse } from '../lib/claude';
import toast from 'react-hot-toast';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  files?: File[];
}

interface AIChatTabProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

const AIChatTab: React.FC<AIChatTabProps> = ({ messages, setMessages }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleChatSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!message.trim()) return;

    const newMessage: Message = {
      role: 'user',
      content: message
    };

    setMessages(prev => [...prev, newMessage]);
    setMessage('');
    setIsLoading(true);

    try {
      const response = await generateClaudeResponse([...messages, newMessage]);
      if (response) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response
        }]);
      }
    } catch (error) {
      console.error('Error generating response:', error);
      toast.error(t('chat.error', 'Error generating response'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderWelcomeMessage = () => {
    // Only show welcome message if there are no messages
    if (messages.length > 0) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-start relative"
      >
        <div className="relative max-w-[90%] px-4 py-3 rounded-lg bg-slate-100 dark:bg-gray-700/50 text-slate-800 dark:text-gray-200 border border-slate-200 dark:border-gray-700">
          <div className="whitespace-pre-line leading-relaxed">
            {t('chat.welcomeMessage')}
          </div>
        </div>
      </motion.div>
    );
  };

  const linkify = (text: string) => {
    const regex = /(https?:\/\/[^\s]+|mailto:[^\s]+|tel:[^\s]+)/g;
    const parts = text.split(regex);
    return parts.map((part, idx) => {
      if (/^https?:\/\//.test(part) || /^mailto:/.test(part) || /^tel:/.test(part)) {
        return (
          <a
            key={`lnk-${idx}`}
            href={part}
            target={part.startsWith('http') ? '_blank' : undefined}
            rel={part.startsWith('http') ? 'noopener noreferrer' : undefined}
            className="inline-flex items-center px-2 py-1 rounded-md bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border border-sky-100 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900/50 mr-1"
          >
            {part}
          </a>
        );
      }
      return <React.Fragment key={`txt-${idx}`}>{part}</React.Fragment>;
    });
  };

  return (
    <>
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto p-4 space-y-5 bg-white dark:bg-gray-800"
      >
        {messages.length === 0 && renderWelcomeMessage()}
        
        {messages.map((msg, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 * index }}
            className={`flex ${
              msg.role === 'user' ? 'justify-end' : 'justify-start'
            } relative`}
          >
            <div
              className={`relative max-w-[90%] px-4 py-3 rounded-lg ${
                msg.role === 'user'
                  ? 'bg-sky-50 dark:bg-sky-900/30 text-slate-900 dark:text-gray-100 border border-sky-100 dark:border-sky-800'
                  : 'bg-slate-100 dark:bg-gray-700/50 text-slate-800 dark:text-gray-200 border border-slate-200 dark:border-gray-700'
              }`}
            >
              <div className="whitespace-pre-line leading-relaxed">
                {msg.content.split('\n').map((line, i) => (
                  <p key={`ln-${i}`} className="mb-1">{linkify(line)}</p>
                ))}
              </div>
              
              {msg.files && msg.files.length > 0 && (
                <div className="mt-2 space-y-2">
                  {msg.files.map((file, fileIndex) => (
                    <div
                      key={fileIndex}
                      className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border border-slate-200 dark:border-gray-700"
                    >
                      <FileText className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                      <span className="text-sm truncate text-slate-900 dark:text-gray-100">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-start relative"
          >
            <div className="relative max-w-[90%] px-4 py-3 rounded-lg bg-slate-100 dark:bg-gray-700/50 text-slate-800 dark:text-gray-200 border border-slate-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-sky-600 dark:text-sky-400" />
                <span className="text-slate-600 dark:text-gray-300">{t('chat.thinking', 'Thinking...')}</span>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <form
        onSubmit={handleChatSubmit}
        className="p-4 border-t border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex gap-4 items-end"
      >
        <div className="flex-1 min-h-12 max-h-48">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('chat.placeholder', 'Type a message...')}
            className="w-full h-full min-h-12 max-h-48 bg-white dark:bg-gray-700/50 border border-slate-300 dark:border-gray-600 rounded-md px-4 py-3 text-slate-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-1 focus:ring-sky-600 dark:focus:ring-sky-500 focus:border-sky-600 dark:focus:border-sky-500 placeholder-slate-400 dark:placeholder-gray-400"
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          className="p-3 bg-sky-600 dark:bg-sky-700 rounded text-white transition-all focus:outline-none focus:ring-1 focus:ring-sky-600/30 disabled:opacity-50 disabled:pointer-events-none self-end shadow-md min-w-[50px] min-h-[50px] flex items-center justify-center hover:bg-sky-700 dark:hover:bg-sky-600"
          disabled={!message.trim() || isLoading}
          style={{}}
        >
          {isLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Send className="w-6 h-6 text-white" />
          )}
        </button>
      </form>
    </>
  );
};

export default AIChatTab; 